import axios from 'axios'
import { useProductStore } from '../pinia/ProductStore'

const SECTION_SELECTOR = '[data-cans-section]'
export const CANS_SELECTORS_UPDATED_EVENT = 'cans:selectors-updated'

let delegationBound = false

export function initCansSiblingDelegation () {
  if (delegationBound) return
  delegationBound = true
  document.addEventListener('click', onCansSectionClick, true)
}

function cssEscape (value) {
  const s = String(value)
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(s)
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function normalizeProductPath (productUrl) {
  const raw = (productUrl || '').trim()
  if (!raw || raw === '#') return ''
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      const u = new URL(raw)
      return `${u.pathname}${u.search}`
    } catch {
      return raw
    }
  }
  return raw.startsWith('/') ? raw : `/${raw}`
}

function buildCansParentPageUrl (parentHandle, htmlView, optionValues) {
  const params = new URLSearchParams()
  if (htmlView) params.set('view', htmlView)
  if (optionValues) params.set('option_values', optionValues)
  const qs = params.toString()
  return `${window.Shopify.routes.root}products/${parentHandle}${qs ? `?${qs}` : ''}`
}

function parseSelectedVariantIdFromPageHtml (pageHTML) {
  if (!pageHTML) return null
  try {
    const doc = new DOMParser().parseFromString(pageHTML, 'text/html')
    const holder =
      doc.querySelector('cans-pack-selector[data-selected-variant-id]') ||
      doc.querySelector('[data-selected-variant-id]')
    const raw = holder?.getAttribute?.('data-selected-variant-id')
    const vid = raw ? parseInt(String(raw).trim(), 10) : NaN
    return Number.isNaN(vid) ? null : vid
  } catch (e) {
    console.warn('[cans-sibling-swap] parse selected variant id', e)
    return null
  }
}

function patchCansSelectorsFromFetchedHtml (sectionEl, pageHTML) {
  const doc = new DOMParser().parseFromString(pageHTML, 'text/html')
  const fetchedRoot =
    doc.querySelector('.product-cans-swap-root[data-cans-section]') ||
    doc.querySelector('[data-cans-section]')
  if (!fetchedRoot) return

  const fetchedFlavour = fetchedRoot.querySelector('cans-flavour-row.product__cans-flavour-row')
  const fetchedPack = fetchedRoot.querySelector('cans-pack-selector.product__cans-pack-selector')

  const liveFlavour = sectionEl.querySelector('cans-flavour-row.product__cans-flavour-row')
  const livePack = sectionEl.querySelector('cans-pack-selector.product__cans-pack-selector')

  if (fetchedFlavour && liveFlavour) {
    const liveInner = liveFlavour.querySelector('.product__cans-flavour-row__inner')
    if (liveInner) {
      liveInner.innerHTML = fetchedFlavour.innerHTML
    } else {
      liveFlavour.innerHTML = fetchedFlavour.innerHTML
    }
  }

  if (fetchedPack && livePack) {
    const selVid = fetchedPack.getAttribute('data-selected-variant-id')
    if (selVid) livePack.setAttribute('data-selected-variant-id', selVid)
    const liveInner = livePack.querySelector('.product__cans-pack-selector__inner')
    if (liveInner) {
      liveInner.innerHTML = fetchedPack.innerHTML
    } else {
      livePack.innerHTML = fetchedPack.innerHTML
    }
    notifyCansSelectorsUpdated(livePack)
  }
}

function notifyCansSelectorsUpdated (packHost) {
  packHost?.dispatchEvent?.(new CustomEvent(CANS_SELECTORS_UPDATED_EVENT))
}

function variantExistsOnParentProduct (store, variantId) {
  const id = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId
  return store.product?.variants?.some(
    (v) => v.id === id || String(v.id) === String(variantId)
  )
}

/**
 * O(1) resolve from Liquid-built map. `optionValues` must match json-product
 * combinedListingVariantByOptionValueKey keys / ?option_values= (pairs from
 * buildOptionPairs are already sorted by option position).
 */
function resolveVariantIdFromCombinedMap (store, optionValues) {
  if (!optionValues || !String(optionValues).includes(',')) return null
  const map = store.combinedListingVariantByOptionValueKey || {}
  const raw = map[optionValues]
  if (raw == null) return null
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10)
  return Number.isNaN(n) ? null : n
}

function syncCansSelectorChrome (swapRoot, pairs, selectedVariantId) {
  if (!swapRoot || !pairs?.length) return

  const flavourHost = swapRoot.querySelector(
    'cans-flavour-row.product__cans-flavour-row'
  )
  const packHost = swapRoot.querySelector(
    'cans-pack-selector.product__cans-pack-selector'
  )
  const flavourPos = parseInt(
    flavourHost?.getAttribute?.('data-option-position') || '0',
    10
  )
  const packPos = parseInt(
    packHost?.getAttribute?.('data-option-position') || '0',
    10
  )

  const setRowActiveByOptionId = (host, optionValueId) => {
    if (!host || !optionValueId) return
    host.querySelectorAll('li[role="radio"]').forEach((li) => {
      const hit = li.querySelector(
        `a[data-option-value-id="${cssEscape(String(optionValueId))}"]`
      )
      const on = !!hit
      li.classList.toggle('is-active', on)
      li.setAttribute('aria-checked', on ? 'true' : 'false')
    })
  }

  for (const p of pairs) {
    if (p.position === flavourPos) {
      setRowActiveByOptionId(flavourHost, p.id)
    }
    if (p.position === packPos) {
      setRowActiveByOptionId(packHost, p.id)
    }
  }

  if (selectedVariantId != null && packHost) {
    packHost.setAttribute('data-selected-variant-id', String(selectedVariantId))
  }

  notifyCansSelectorsUpdated(packHost)
}

async function onCansSectionClick (event) {
  let link = event.target.closest?.(
    'a[data-product-url][data-option-value-id]'
  )
  if (!link) {
    const optionRow = event.target.closest?.(
      '.product__cans-flavour-row li[role="radio"], .product__cans-pack-selector li[role="radio"]'
    )
    link = optionRow?.querySelector?.(
      'a[data-product-url][data-option-value-id]'
    )
  }
  if (!link) return

  const sectionEl = link.closest(SECTION_SELECTOR)
  if (!sectionEl) return

  const flavourHost = link.closest('.product__cans-flavour-row')
  const packHost = link.closest('.product__cans-pack-selector')
  if (!flavourHost && !packHost) return

  if (link.getAttribute('aria-disabled') === 'true') {
    const href = (link.getAttribute('href') || '').trim()
    if (!href || href === '#') {
      event.preventDefault()
      return
    }
  }

  const productUrl = normalizeProductPath(link.dataset.productUrl || '')
  if (!productUrl) {
    event.preventDefault()
    return
  }

  event.preventDefault()

  const clickedId = link.dataset.optionValueId
  const clickedPos = parseInt(link.dataset.optionPosition || '0', 10)

  const shared = {
    sectionEl,
    productUrl,
    clickedOptionValueId: clickedId,
    clickedOptionPosition: clickedPos
  }

  if (flavourHost) {
    await cansSiblingSwap({
      ...shared,
      rootEl: flavourHost,
      otherRelativeSelector:
        '.product__cans-pack-selector li[role="radio"].is-active a[data-option-value-id]',
      focusSelector: `a[data-option-value-id="${cssEscape(clickedId)}"]`
    })
    return
  }

  await cansSiblingSwap({
    ...shared,
    rootEl: packHost,
    otherRelativeSelector:
      '.product__cans-flavour-row li[role="radio"].is-active a[data-option-value-id]',
    focusSelector: `a[data-option-value-id="${cssEscape(clickedId)}"]`
  })
}

export async function cansSiblingSwap ({
  rootEl,
  sectionEl,
  productUrl,
  clickedOptionValueId,
  clickedOptionPosition,
  otherRelativeSelector,
  focusSelector
}) {
  let swapRoot = sectionEl
  if (!swapRoot?.hasAttribute?.('data-cans-section')) {
    swapRoot = rootEl.closest(SECTION_SELECTOR)
  }
  if (!swapRoot) {
    console.error('[cans-sibling-swap] section root not found; aborting')
    return
  }

  optimisticActiveSwap(rootEl, clickedOptionValueId)

  const pairs = buildOptionPairs({
    clickedOptionValueId,
    clickedOptionPosition,
    sectionEl: swapRoot,
    otherRelativeSelector
  })
  const optionValues = pairs.map((p) => String(p.id)).join(',')

  const store = useProductStore()
  const parentHandle = store.product?.handle
  if (!parentHandle) {
    console.error('[cans-sibling-swap] no parent product handle in store')
    return
  }

  const viewParam = new URLSearchParams(window.location.search).get('view')
  const htmlView = viewParam || 'combined-cans-v2'

  let variantId = resolveVariantIdFromCombinedMap(store, optionValues)

  if (variantId != null && variantExistsOnParentProduct(store, variantId)) {
    try {
      await store.changeSelectedVariant(variantId)
      syncCansSelectorChrome(swapRoot, pairs, variantId)
      restoreFocus(swapRoot, focusSelector)
      return
    } catch (err) {
      console.error('[cans-sibling-swap] fast path failed', err)
    }
  }

  const pageUrl = buildCansParentPageUrl(parentHandle, htmlView, optionValues)
  let pageHTML = ''
  try {
    const response = await axios.get(pageUrl)
    pageHTML = response.data
  } catch (err) {
    console.error('[cans-sibling-swap] fetch failed', err)
    fallbackToFullNav(normalizeProductPath(productUrl), viewParam, optionValues)
    return
  }

  variantId = parseSelectedVariantIdFromPageHtml(pageHTML)
  if (variantId == null) {
    console.error('[cans-sibling-swap] could not parse data-selected-variant-id from HTML')
    fallbackToFullNav(normalizeProductPath(productUrl), viewParam, optionValues)
    return
  }

  if (!variantExistsOnParentProduct(store, variantId)) {
    console.error(
      '[cans-sibling-swap] resolved variant not in parent combined product',
      variantId
    )
    fallbackToFullNav(normalizeProductPath(productUrl), viewParam, optionValues)
    return
  }

  try {
    await store.changeSelectedVariant(variantId)
    patchCansSelectorsFromFetchedHtml(swapRoot, pageHTML)
    restoreFocus(swapRoot, focusSelector)
  } catch (err) {
    console.error('[cans-sibling-swap] in-place update failed; falling back to full nav', err)
    fallbackToFullNav(normalizeProductPath(productUrl), viewParam, optionValues)
  }
}

function optimisticActiveSwap (rootEl, clickedOptionValueId) {
  rootEl.querySelectorAll('[role="radio"].is-active').forEach((el) => {
    el.classList.remove('is-active')
    el.setAttribute('aria-checked', 'false')
  })
  const clicked = rootEl.querySelector(
    `[data-option-value-id="${cssEscape(clickedOptionValueId)}"]`
  )
  const radio = clicked?.closest('[role="radio"]')
  if (radio) {
    radio.classList.add('is-active')
    radio.setAttribute('aria-checked', 'true')
  }
}

function buildOptionPairs ({
  clickedOptionValueId,
  clickedOptionPosition,
  sectionEl,
  otherRelativeSelector
}) {
  const pairs = [
    {
      position: clickedOptionPosition || 0,
      id: String(clickedOptionValueId)
    }
  ]
  const otherEl = sectionEl.querySelector(otherRelativeSelector)
  const otherId = otherEl?.dataset?.optionValueId
  if (otherId) {
    pairs.push({
      position: parseInt(otherEl.dataset.optionPosition || '0', 10) || 0,
      id: String(otherId)
    })
  }
  pairs.sort((a, b) => a.position - b.position)
  return pairs
}

function restoreFocus (newSection, focusSelector) {
  if (!focusSelector) return
  newSection.querySelector(focusSelector)?.focus?.({ preventScroll: true })
}

function fallbackToFullNav (productPath, viewParam, optionValues) {
  const path = normalizeProductPath(productPath)
  const fallback = new URL(path || window.location.pathname, window.location.origin)
  if (viewParam) fallback.searchParams.set('view', viewParam)
  if (optionValues) fallback.searchParams.set('option_values', optionValues)
  window.location.href = fallback.toString()
}
